import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { readAnswer } from "../badge/badge";
import { useEvent } from "../context/EventProvider";
import { sendConfirmation } from "../emails/confirmation";
import { logParticipation, stepsOf } from "../log/participation";
import { useMode } from "../panel/mode";
import { withContext } from "../routes/registry";
import { useLocale, useSettings, useT } from "../settings/context";
import { Confirm } from "../ui/Confirm";
import { useAnswers, type Answers } from "./answers";
import {
  cleanSelection,
  droppedByDays,
  euros,
  fetchResume,
  flatIds,
  type Article,
  type Resume,
  type Selection,
} from "./articles";

import { longDay, selectedDaysOf } from "./dates";
import { markWritten } from "./leaveGuard";
import { daysToWrite, isRegistered, type Recorded } from "./participant";
import { CONFIRMATION, inThread } from "./thread";
import { fetchSavedResume, useArticles } from "./useArticles";
import { useOffer } from "./useOffre";
import { useParticipation } from "./useParticipation";

/**
 * ═══ ENREGISTRER DEPUIS UNE ÉTAPE, SANS ALLER JUSQU'À LA CONFIRMATION ═══
 *
 * Quatre étapes du tunnel ne posaient leurs réponses nulle part : la participation, la
 * typologie, les dates et la billetterie rangent leur choix dans l'état de navigation, et
 * c'est la confirmation qui écrit tout, en fin de parcours. Cela va de soi la première
 * fois. Cela ne va plus du tout quand on REVIENT changer une seule chose : il fallait
 * repousser « Continuer » sur chaque écran restant pour que la correction atteigne enfin
 * l'écran qui écrit. Sur téléphone, c'était le seul chemin possible : le fil d'Ariane
 * cliquable est réservé au bureau (`.steps__rail`, voir `styles/desktop.css`).
 *
 * ⚠️ CE N'EST PAS UNE SECONDE ÉCRITURE, C'EST LA MÊME. `saveParticipation` est une
 * écriture PARTIELLE depuis le début : ce que l'état de navigation ne porte pas retombe
 * sur ce que le serveur détient (voir `funnel/participant.ts`, la note sur la
 * désinscription accidentelle). Appeler la mutation depuis une étape écrit donc
 * exactement ce que la confirmation aurait écrit, sans rien effacer au passage.
 *
 * ⚠️ ET ELLE ÉCRIT POUR DE VRAI EN MODE ORGANISATEUR, comme le reste du tunnel : décidé
 * le 2026-08-08, neutraliser les écritures cacherait de vrais défauts à l'organisateur
 * qui règle son parcours en le parcourant.
 *
 * Les textes affichés sont français : c'est un produit français.
 */

/**
 * LA COMMANDE À RÉÉCRIRE QUAND ON ENREGISTRE SANS ÊTRE PASSÉ PAR LA BILLETTERIE.
 *
 * ⚠️ SANS ELLE, DÉCOCHER UNE JOURNÉE LAISSE SON ARTICLE FACTURÉ. `saveParticipation`
 * saute entièrement l'écriture de la commande quand l'état ne porte pas d'articles, et
 * c'est voulu : la route DÉTRUIT la commande avant de la recréer, donc l'appeler à vide
 * effacerait une commande déjà payée. Mais le nettoyage par jour retenu
 * (`nettoieSelection`) vit dans cette même écriture. Traverser tout le parcours passait
 * par l'étape billetterie, qui renvoyait sa sélection et déclenchait le filtre ;
 * enregistrer depuis les dates ne passe plus par elle, et l'article du mardi survivait
 * au décochage du mardi.
 *
 * On renvoie donc la commande DÉJÀ ENREGISTRÉE, telle quelle : c'est l'écriture qui la
 * refiltre contre les jours retenus et contre le catalogue. Rien n'est ajouté, rien
 * n'est deviné.
 *
 * ⚠️ ET SEULEMENT QUAND LES JOURS CHANGENT VRAIMENT. Réécrire une commande identique
 * ferait détruire puis recréer des lignes payées à chaque « Enregistrer », pour rien.
 *
 * ⚠️ UN REFUS NE PASSE PAS PAR ICI, ET C'EST DÉLIBÉRÉ. Décliner retire tous les jours,
 * donc le test « les jours ont changé » serait vrai et la commande y passerait tout
 * entière à la trappe. Le tunnel, lui, ne l'a jamais fait : un refus mène droit à la
 * confirmation sans traverser la billetterie, donc la commande reste en place. Détruire
 * ce qui a été payé est une décision de produit, pas un effet de bord d'un bouton.
 */
export function orderToRewrite({
  answers,
  recorded,
  days,
  saved,
}: {
  /** Ce qui vient d'être répondu, l'étape en cours comprise. */
  answers: Answers;
  /** Ce que le serveur détient. */
  recorded: Pick<Recorded, "answer" | "days"> | undefined;
  /** Tous les jours de l'événement. */
  days: ReadonlyArray<number>;
  /** La commande enregistrée, quand on la connaît. */
  saved: Selection | undefined;
}): Selection | undefined {
  // L'étape billetterie a été traversée : sa réponse fait foi, il n'y a rien à
  // reconstituer.
  if (answers.articles) return undefined;
  /**
   * On ne connaît pas la commande. On ne la devine pas : la route détruit avant d'écrire,
   * et une commande devinée à vide serait une commande effacée.
   *
   * ⚠️ CE QUE CE CHOIX COÛTE, DIT ICI PLUTÔT QUE DÉCOUVERT PLUS TARD : si la lecture de la
   * commande échoue (deux requêtes, un réseau qui hoquette), le jour se décoche et son
   * article RESTE facturé, sans un mot. On a préféré ce risque-là à celui d'écraser une
   * commande réglée sur une lecture qu'on n'a pas eue. Le tunnel complet, lui, repasse par
   * l'étape billetterie, qui refuse le clic tant qu'elle n'a pas lu.
   */
  if (!saved) return undefined;
  // Les jours n'ont pas été rejoués ici, donc rien n'a pu se décocher.
  if (!answers.days || !recorded?.days) return undefined;

  const answer = answers.participation ?? recorded.answer;
  if (answer !== "onsite" && answer !== "remote") return undefined;

  /* Les deux états comparés par la MÊME règle que l'écriture, et non par une seconde
     lecture des mêmes champs : `joursRetenus` filtre contre les jours réels de
     l'événement et déduplique, donc deux listes égales s'écrivent pareil. */
  const after = selectedDaysOf({
    coming: true,
    chosen: answers.days,
    saved: recorded.days,
    all: days,
  });
  const before = selectedDaysOf({
    coming: true,
    saved: recorded.days,
    all: days,
  });
  return after.join() === before.join() ? undefined : saved;
}

/**
 * ═══ UN BILLET RÉGLÉ NE TOMBE PAS SANS QU'ON L'AIT DIT (tâche #212) ═══
 *
 * Décocher le mardi dans « Mes jours » puis enregistrer retirait le déjeuner du mardi,
 * déjà payé, sans un mot : l'écriture refiltre la commande contre les jours retenus
 * (voir `orderToRewrite`), et l'écran des jours ne montre aucun billet.
 *
 * ⚠️ PRÉVENIR ET FAIRE CONFIRMER, NI BLOQUER NI METTRE DE CÔTÉ. La billetterie laisse
 * déjà retirer un article réglé, et annonce alors « Trop-perçu · un avoir vous sera
 * fait » : refuser ici le même retrait aurait fait deux règles pour un même geste. Mettre
 * le billet de côté pour le rendre au jour recoché aurait demandé une table côté serveur,
 * et rendu un billet dont l'avoir est peut-être déjà parti. On dit donc lesquels, ce que
 * le trop-perçu devient, et que recocher le jour ne les rendra pas.
 *
 * ⚠️ RÉGLÉ SE LIT AU SOLDE, PAS À LA LIGNE. Le produit ne rattache pas un paiement à un
 * billet : il compare la somme réglée à la commande (voir `funnel/articles.ts`). Un
 * billet pris APRÈS le paiement tombe donc sans question, et c'est juste : il n'est pas
 * payé. On demande le solde avec et sans les billets qui tombent ; ils emportaient de
 * l'argent réglé si le trop-perçu GRANDIT. Un trop-perçu qui existait déjà n'en est pas
 * la preuve.
 */

/** Des billets vont tomber avec leurs jours : ce qu'on en dit avant d'écrire. */
export type PaidRemoval = {
  /** Les billets retirés, lisibles : « Déjeuner (Mardi 21 septembre) ». */
  tickets: string;
  /** Le trop-perçu TTC qui en résulte ; absent quand le solde n'a pas pu être lu. */
  credit?: number;
};

/** La question posée, et les deux façons d'y répondre. */
export type RemovalPrompt = {
  pending: PaidRemoval | null;
  accept: () => void;
  refuse: () => void;
};

/**
 * LE TROP-PERÇU APRÈS LE RETRAIT, s'il en crée un ; `null` sinon. Au centime, pour qu'un
 * arrondi du serveur n'invente pas une question.
 */
export function creditFrom(
  before: Pick<Resume, "remainingInclTax">,
  after: Pick<Resume, "remainingInclTax">,
): number | null {
  const over = (r: Pick<Resume, "remainingInclTax">) =>
    Math.max(0, -r.remainingInclTax);
  return Math.round((over(after) - over(before)) * 100) > 0 ? over(after) : null;
}

/**
 * LES BILLETS QUI TOMBENT, NOMMÉS : « Déjeuner, Dîner (Mardi 21 septembre) ; Dîner (Mercredi 22 septembre) », dans
 * l'ordre des jours. Vide quand aucun n'est sur un jour de l'événement (rien que le
 * serveur ait pu garder, voir `droppedByDays`).
 */
export function ticketsOf(
  dropped: Selection,
  articles: ReadonlyArray<Article>,
  days: ReadonlyArray<{ id: number; day: string }>,
  locale: string,
): string {
  const name = (id: number) =>
    articles.find((a) => a.id === id)?.label ?? `Article ${id}`;
  return days
    .flatMap((d) => {
      const ids = dropped[String(d.id)] ?? [];
      return ids.length > 0
        ? [`${ids.map(name).join(", ")} (${longDay(d.day, locale)})`]
        : [];
    })
    .join(" ; ");
}

/** Où en est l'écriture de cette étape. */
export type SaveState = "idle" | "saving" | "done" | "failed";

export function useStepSave({
  /**
   * ═══ OÙ L'ON ATTERRIT UNE FOIS ÉCRIT ═══
   *
   * Dans le TUNNEL, on repart à la confirmation. Trois raisons, et la première n'est pas
   * un confort :
   *
   *   — LE SOLDE NE SE RÈGLE QUE LÀ. Ajouter un article depuis la billetterie crée une
   *     dette, et le bloc de paiement vit sur la confirmation (il facture ce qui est
   *     ENREGISTRÉ, voir `funnel/payment.ts`). Rester sur l'étape laissait quelqu'un
   *     devant une somme due sans aucun moyen de la payer.
   *   — C'EST L'ÉCRAN QUI REND COMPTE : le récapitulatif relit ce que le serveur détient,
   *     donc il montre la correction plutôt que de la promettre.
   *   — L'ÉCRAN D'APRÈS EST LA PREUVE, la règle que l'étape informations écrit déjà pour
   *     sa version servie en page : « en page, on reste, le mot qui suit est la seule
   *     preuve ; dans le tunnel, l'écran d'après est cette preuve ».
   *
   * En PAGE, il n'y a pas de tunnel où revenir : on reste, et c'est la phrase qui prouve.
   *
   * ⚠️ AUJOURD'HUI, SEULES LES PAGES ÉCRIVENT (tâche #546) : dans le tunnel, même en
   * correction, les étapes n'offrent que « Continuer », et la correction s'écrit à la
   * confirmation. Le retour à la confirmation reste écrit pour qui rouvrirait ce geste.
   */
  asPage,
}: { asPage?: boolean } = {}): {
  /**
   * L'INSCRIPTION EXISTE DÉJÀ : on corrige, on ne remplit pas.
   *
   * ⚠️ C'EST `isRegistered`, LA MÊME BASCULE QUE LE FIL D'ARIANE, et il ne peut pas y en
   * avoir une seconde : le fil ouvre toutes ses étapes sur cette question-là (voir
   * `funnel/Steps.tsx`). Un bouton « Enregistrer » qui apparaîtrait sur un autre
   * critère se montrerait à des écrans que le fil tient encore fermés.
   *
   * ⚠️ ET PAS `responded`, QU'UN REFUS POSE AUSSI (bug #522) : « Enregistrer » à l'étape 1
   * inscrivait d'un clic quelqu'un qui avait refusé, sans les étapes suivantes.
   */
  correction: boolean;
  /** L'écriture est possible : l'identité est connue, et lisible. */
  ready: boolean;
  state: SaveState;
  /** Écrit ce parcours, plus ce que l'étape vient de répondre. */
  save: (patch?: Answers) => void;
  /**
   * À rappeler dès qu'on retouche l'écran : « c'est enregistré » ne doit pas rester
   * affiché au-dessus d'une case qu'on vient de décocher depuis.
   */
  touched: () => void;
  /** La question posée avant de retirer des billets réglés, à passer à `SaveNote`. */
  removal: RemovalPrompt;
} {
  const [answers] = useAnswers();
  const { value, days, typos } = useSettings();
  const locale = useLocale();
  const offer = useOffer();
  const navigate = useNavigate();
  const { key } = useLocation();
  const event = useEvent();
  const mode = useMode();
  const client = useQueryClient();
  const {
    me,
    recorded,
    save: mutation,
    loading,
    unavailable,
    lastWriteChanged,
  } = useParticipation();
  const [state, setState] = useState<SaveState>("idle");
  /**
   * ⚠️ DEMANDÉE SEULEMENT QUAND ELLE PEUT SERVIR. Deux requêtes de plus (le catalogue et
   * la commande) sur un écran qui n'en avait aucune : elles ne partent que si
   * l'événement VEND (l'étape est optionnelle, l'immense majorité des événements ne la
   * sert pas). Leur cache est partagé avec l'étape elle-même : y revenir ne les
   * redemande pas, et quand la billetterie a déjà répondu dans ce parcours, elle les a
   * déjà lues. Le catalogue sert à nommer les billets qui tomberaient (tâche #212).
   */
  const { articles, saved } = useArticles(
    inThread(value, "/registration/articles", offer),
    locale,
  );
  /** La question en attente : des billets réglés tomberaient avec leurs jours. */
  const [pending, setPending] = useState<{
    removal: PaidRemoval;
    write: () => void;
  } | null>(null);

  const save = (patch: Answers = {}) => {
    if (state === "saving" || loading || unavailable) return;
    const answered = { ...answers, ...patch };
    const dayIds = days.map((d) => d.id);
    const order = orderToRewrite({
      answers: answered,
      recorded,
      days: dayIds,
      saved: saved,
    });
    /* L'entrée d'historique dont on écrit les réponses — pas celle qu'un clic pendant
       l'écriture aurait posée depuis, qui porte une correction que ceci n'écrit pas. */
    const at = key;
    const go = () => write(answered, order, at);

    /**
     * ⚠️ LA COMMANDE QUE L'ÉCRITURE ENVERRA, ET LES JOURS PAR LESQUELS ELLE LA FILTRERA :
     * les mêmes que `saveParticipation`, par les mêmes fonctions. La billetterie compte
     * aussi : un billet du mardi gardé dans sa sélection y est invisible quand le mardi
     * n'est plus retenu, et il tombe à son « Enregistrer » comme à celui des jours.
     */
    const toWrite = answered.articles ?? order;
    const selected = toWrite ? daysToWrite(answered, recorded ?? {}, dayIds) : null;
    const dropped = toWrite && selected ? droppedByDays(toWrite, selected) : {};
    const tickets = ticketsOf(dropped, articles, days, locale);
    if (!toWrite || !selected || tickets === "" || !event || typeof me?.id !== "number") {
      go();
      return;
    }

    /* Le bouton tourne pendant la question au serveur : c'est déjà l'écriture qui
       commence, et un second clic ne doit pas en lancer une autre. */
    setState("saving");
    const ask = (removal: PaidRemoval) => {
      setState("idle");
      setPending({ removal, write: go });
    };
    /* Les identifiants que le serveur sait chiffrer : le catalogue écarte un article
       périmé, comme à l'écriture ; inconnu, le serveur les écarte lui-même. */
    const idsOf = (s: Selection) =>
      articles.length > 0 ? flatIds(s, articles) : Object.values(s).flat();
    Promise.all(
      [toWrite, cleanSelection(toWrite, { selectedDays: selected })].map((s) =>
        fetchResume(event.domainId, event.eventId, me.id, idsOf(s)),
      ),
    ).then(
      ([before, after]) => {
        const credit = creditFrom(before!, after!);
        if (credit === null) go();
        else ask({ tickets, credit });
      },
      /* Le solde n'a pas pu être lu : on ne sait pas si ces billets sont réglés, donc on
         le demande quand même, sans montant. Retirer un billet payé en silence est
         précisément ce qu'on corrige. */
      () => ask({ tickets }),
    );
  };

  const write = (answered: Answers, order: Selection | undefined, at: string) => {
    setState("saving");
    /* L'écriture part en mode organisateur aussi (voir l'en-tête) ; l'email, jamais :
       c'est la règle de la confirmation, relevée au moment où l'on écrit. */
    const emailed = mode === "visitor";
    // Lu avant l'écriture : après, le cache dit déjà « inscrit » (`log/participation.ts`).
    const registeredBefore = isRegistered(recorded);
    mutation
      .mutateAsync(order ? { ...answered, articles: order } : answered)
      .then((written) => {
        // Écrit : le garde de sortie la laisse partir (voir `funnel/leaveGuard.ts`).
        markWritten(at);
        setState("done");
        if (emailed) confirmByEmail(answered, written);
        logParticipation(event, () => {
          const typoId = answered.typoId ?? written?.typoId ?? recorded?.typoId;
          return {
            wasRegistered: registeredBefore,
            answer: readAnswer(answered.participation ?? written?.answer ?? recorded?.answer),
            typo: typos.find((t) => t.id === typoId)?.name,
            steps: stepsOf(answered),
            // Rien de changé chez le serveur, rien au journal (`participationChanged`).
            changed: lastWriteChanged?.(),
          };
        });
        if (asPage) return;
        /**
         * ⚠️ SANS LES RÉPONSES, ET C'EST TOUT CE QUI SÉPARE UN COMPTE RENDU D'UNE SECONDE
         * ÉCRITURE. La confirmation écrit ce que l'état de navigation porte, dès son
         * arrivée (voir `aRepondu`) : emportées, les réponses qu'on vient d'enregistrer y
         * seraient réécrites intégralement — jours re-datés, et surtout commande DÉTRUITE
         * puis recréée, lignes déjà réglées comprises. Vidé, l'état la laisse faire ce
         * pour quoi on l'ouvre : relire, et rendre compte.
         *
         * ⚠️ ET C'EST SÛR PARCE QU'IL Y A UNE INSCRIPTION : cet écran ne renvoie au début
         * que s'il ne trouve NI réponse en main NI inscription enregistrée, or on vient
         * d'en écrire une.
         */
        navigate(withContext(CONFIRMATION, event?.token), { state: {} });
      })
      .catch(() => setState("failed"));
  };

  /**
   * ═══ UNE CORRECTION ENREGISTRÉE EST CONFIRMÉE PAR EMAIL, COMME LA FIN DU TUNNEL ═══
   *
   * ⚠️ C'EST ICI ET NON À LA CONFIRMATION, parce que celle-ci n'écrit rien quand on y
   * arrive par « Enregistrer » (l'état est vidé, voir plus haut), donc elle n'envoie rien
   * non plus — et en page, on n'y va même pas. La correction faite d'ici était ainsi
   * enregistrée sans un email, quand traverser tout le tunnel en faisait partir un
   * (tâche #221). Qui écrit, envoie : un seul envoi par écriture, et aucun au
   * rechargement.
   *
   * ⚠️ LE SOLDE D'ABORD, RELU : la règle de la confirmation, « rien tant qu'il reste à
   * payer » (voir `emails/confirmation.ts`). Une lecture qui échoue n'empêche pas l'envoi,
   * comme à la confirmation, où un solde inconnu ne retient rien.
   *
   * La réponse est celle qu'on vient d'écrire, sinon celle que l'écriture rend : une
   * correction des jours ne porte pas la participation dans l'état.
   */
  const confirmByEmail = (answered: Answers, written: Recorded | undefined) => {
    if (!event || typeof me?.id !== "number") return;
    const who = me;
    const answer = readAnswer(
      answered.participation ?? written?.answer ?? recorded?.answer,
    );
    void fetchSavedResume(client, event.domainId, event.eventId, who.id)
      .then(
        (resume) => resume.toPay,
        () => undefined,
      )
      .then((toPay) => {
        if (toPay !== true)
          sendConfirmation(event, who, answer, answered.stand?.email);
      });
  };

  return {
    correction: isRegistered(recorded),
    ready: !loading && !unavailable,
    state,
    save,
    // Reposer le même état ne rend pas : React s'arrête sur une valeur identique.
    touched: () => setState("idle"),
    removal: {
      pending: pending?.removal ?? null,
      accept: () => {
        setPending(null);
        pending?.write();
      },
      // Rien n'est écrit : le jour reste décoché à l'écran, et l'on peut le recocher.
      refuse: () => setPending(null),
    },
  };
}

/**
 * CE QUE L'ÉCRITURE A DONNÉ, DIT PLUTÔT QUE DEVINÉ.
 *
 * ⚠️ LE SUCCÈS NE SE DIT QU'EN PAGE, et c'est la règle de l'étape informations, qui pose
 * la même paire au même endroit : « en page, on reste, le mot qui suit est la seule preuve
 * que le bouton a servi ; dans le tunnel, l'écran d'après est cette preuve » — et depuis
 * qu'enregistrer y ramène à la confirmation, cet écran-là EST la preuve.
 *
 * ⚠️ L'ÉCHEC, LUI, SE DIT DES DEUX CÔTÉS : rien n'a été écrit, donc on ne va nulle part,
 * et un bouton qui rend la main sans un mot se relit comme un bouton mort.
 *
 * ⚠️ L'ÉCHEC EST ANNONCÉ (`role="alert"`), pas seulement écrit : quelqu'un qui ne voit
 * pas l'écran ne doit pas repartir en croyant sa correction enregistrée. C'est le seul
 * mensonge que cet écran puisse faire, et il coûte une place à la porte.
 */
export function SaveNote({
  state,
  removal,
}: {
  state: SaveState;
  /** La question d'avant l'écriture, quand l'étape peut retirer des billets réglés. */
  removal?: RemovalPrompt;
}) {
  return (
    <>
      <SaveOutcome state={state} />
      {removal && <RemovalConfirm removal={removal} />}
    </>
  );
}

function SaveOutcome({ state }: { state: SaveState }) {
  const t = useT();
  if (state === "failed") {
    return (
      <p className="f--wide loginerr" role="alert">
        {t(
          "CHANGES_NOT_SAVED",
          "Vos modifications n'ont pas pu être enregistrées. Réessayez, et si cela persiste, prévenez l'organisateur.",
        )}
      </p>
    );
  }
  if (state === "done") {
    return (
      <p className="f--wide step__ok" role="status">
        {t("YOUR_CHANGES_SAVED", 'Vos modifications sont enregistrées.')}
      </p>
    );
  }
  return null;
}

/**
 * ═══ « RETIRER CES BILLETS ? » ═══ voir `PaidRemoval` pour ce qui la déclenche.
 *
 * ⚠️ LE BOUTON MIS EN AVANT EST « ANNULER ». La question existe pour rattraper un
 * décochage dont on ignorait la portée : un second clic machinal doit tomber sur la
 * sortie sûre, pas sur le retrait (voir `emphasis` dans `ui/Confirm.tsx`).
 *
 * ⚠️ LE MONTANT EST CELUI QUE LA BILLETTERIE AFFICHERAIT, son « Trop-perçu · un avoir
 * vous sera fait », en toutes taxes : c'est la somme qu'on a sortie, et celle qu'on rend.
 */
function RemovalConfirm({ removal }: { removal: RemovalPrompt }) {
  const t = useT();
  const locale = useLocale();
  const { pending } = removal;
  return (
    <Confirm
      open={pending !== null}
      title={t("REMOVE_PAID_TICKETS_ASK", "Retirer ces billets ?")}
      message={
        pending?.credit === undefined
          ? t(
              "REMOVE_TICKETS_MAYBE_PAID_WARNING",
              "Les jours que vous ne retenez plus portent des billets : $1. Ils seront retirés de votre commande ; s'ils sont déjà réglés, un avoir vous sera fait. Recocher ces jours plus tard ne les rendra pas.",
              [pending?.tickets ?? ""],
            )
          : t(
              "REMOVE_PAID_TICKETS_WARNING",
              "Les jours que vous ne retenez plus portent des billets : $1. Ils seront retirés de votre commande déjà réglée, et le trop-perçu de $2 vous sera rendu par un avoir. Recocher ces jours plus tard ne les rendra pas.",
              [
                pending.tickets,
                t("AMOUNT_INCL_TAX", "$1 TTC", [euros(pending.credit, locale)]),
              ],
            )
      }
      confirmLabel={t("REMOVE_THE_TICKETS", "Retirer les billets")}
      onConfirm={removal.accept}
      onCancel={removal.refuse}
      emphasis="cancel"
    />
  );
}
